// Utility functions for mapping between database and frontend provider types
import { WorkStatus, WorkingHoursEntityType } from '@constants/Providers'
import {
  ProviderData,
  ProviderPersonalInfo,
  ProviderWorkInfo,
  ProviderBasicInfo,
  Provider,
  WorkingSchedule,
  WorkOverrideRow,
} from '@dataTypes/provider.types'

const isoDay = (s: string): boolean => /^\d{4}-\d{2}-\d{2}$/.test(s)

/**
 * Maps database provider data to frontend ProviderData type
 */
export const mapDatabaseToProviderData = (
  dbProvider: Provider,
): ProviderData => {
  return {
    // Basic Information
    providerId: dbProvider.provider_id || '',
    userId: dbProvider.user_id,
    clinicId: dbProvider.clinic_id,
    clinicIds: dbProvider.assigned_clinics?.map((c) => c.clinic_id),
    operatoryId: dbProvider.operatory_id || '',

    // Personal Information
    firstName: dbProvider.first_name,
    lastName: dbProvider.last_name,
    middleName: dbProvider.middle_name,
    username: dbProvider.username,
    gender: dbProvider.gender,
    dateOfBirth: dbProvider.date_of_birth,
    pronouns: dbProvider.pronouns,
    maritalStatus: dbProvider.marital_status,

    // Contact Information
    address: dbProvider.address,
    city: dbProvider.city,
    state: dbProvider.state,
    zip: dbProvider.zip,
    mobile: dbProvider.cellphone,
    email: dbProvider.email,
    phone: dbProvider.home_number,
    work: dbProvider.work_contact,
    fax: dbProvider.fax_number ?? '',

    // Professional Information
    stateLicenseNumber: dbProvider.state_license_number,
    stateWhereLicensed: dbProvider.state_where_licensed_issued,
    expiryDate: dbProvider.state_license_expiry,
    stateRxId: dbProvider.state_rx_id,
    deaNumber: dbProvider.dea_number,
    taxIdType: dbProvider.tax_id_type,
    taxIdNumber: dbProvider.tax_id_number,
    socialSecurityNumber: dbProvider.social_security_number,
    npiType: dbProvider.npi_type,
    npiNumber: dbProvider.npi_number || '',
    employmentType: dbProvider.employment_type,
    workRole: dbProvider.work_role,

    // Appearance & UI
    providerColor: dbProvider.provider_color,

    // Work Schedule
    shiftStartTime: dbProvider.shift_start_time,
    shiftEndTime: dbProvider.shift_end_time,
    sameWorkingHours: dbProvider.same_working_hours,
    workStatus: dbProvider.work_status,
    ...(() => {
      const rows =
        dbProvider.workingHours?.map((wh) => ({
          working_hours_id: wh.working_hours_id,
          entity_type: wh.entity_type,
          entity_id: wh.entity_id,
          day_of_week: wh.day_of_week,
          start_time: wh.start_time,
          end_time: wh.end_time,
        })) || []
      const weeks: WorkingSchedule[] = []
      const ovr: WorkOverrideRow[] = []
      for (const r of rows) {
        if (isoDay(r.day_of_week)) {
          ovr.push({
            id: r.working_hours_id || `ov-${r.day_of_week}-${r.start_time}`,
            name: '',
            date: r.day_of_week,
            start_time: r.start_time,
            end_time: r.end_time,
          })
        } else {
          weeks.push(r)
        }
      }
      return { workingSchedules: weeks, workOverrides: ovr }
    })(),

    // Operatory Rooms
    operatoryRooms: dbProvider.operatory_rooms,

    // System Fields
    isHidden: dbProvider.is_hidden,
    createdBy: dbProvider.created_by,
    updatedBy: dbProvider.updated_by,
    dateTimeCreatedAt: dbProvider.date_time_created_at,
    dateTimeUpdatedAt: dbProvider.date_time_updated_at,
  }
}

export const removeEmptyValues = <T extends Record<string, unknown>>(
  obj: T,
): Partial<T> => {
  const result: Partial<T> = {}

  for (const [key, value] of Object.entries(obj)) {
    if (value !== null && value !== undefined && value !== '') {
      result[key as keyof T] = value as T[keyof T]
    }
  }

  return result
}

/**
 * Returns only the provider data fields that changed between initial and current.
 * Used for PUT updates so only changed values are sent.
 */
export const getChangedProviderFields = (
  initial: ProviderData,
  current: ProviderData,
): Partial<ProviderData> => {
  const keySet = new Set([
    ...(Object.keys(initial) as (keyof ProviderData)[]),
    ...(Object.keys(current) as (keyof ProviderData)[]),
  ])
  const changed: Partial<ProviderData> = {}
  const keys = Array.from(keySet)
  for (const key of keys) {
    if (key === 'providerId' || key === 'userId') continue
    const a = initial[key as keyof ProviderData]
    const b = current[key as keyof ProviderData]
    const aJson = typeof a === 'object' && a !== null ? JSON.stringify(a) : a
    const bJson = typeof b === 'object' && b !== null ? JSON.stringify(b) : b
    if (aJson !== bJson) {
      ;(changed as Record<string, unknown>)[key as string] = b
    }
  }
  return changed
}

/** ProviderData key -> database (Provider) key for partial update mapping */
const PROVIDER_DATA_TO_DB_KEYS: Partial<
  Record<keyof ProviderData, keyof Provider>
> = {
  providerId: 'provider_id',
  userId: 'user_id',
  clinicId: 'clinic_id',
  operatoryId: 'operatory_id',
  firstName: 'first_name',
  middleName: 'middle_name',
  lastName: 'last_name',
  username: 'username',
  gender: 'gender',
  dateOfBirth: 'date_of_birth',
  pronouns: 'pronouns',
  maritalStatus: 'marital_status',
  address: 'address',
  city: 'city',
  state: 'state',
  zip: 'zip',
  mobile: 'cellphone',
  email: 'email',
  phone: 'home_number',
  work: 'work_contact',
  fax: 'fax_number',
  stateLicenseNumber: 'state_license_number',
  stateWhereLicensed: 'state_where_licensed_issued',
  expiryDate: 'state_license_expiry',
  stateRxId: 'state_rx_id',
  deaNumber: 'dea_number',
  taxIdType: 'tax_id_type',
  taxIdNumber: 'tax_id_number',
  socialSecurityNumber: 'social_security_number',
  npiType: 'npi_type',
  npiNumber: 'npi_number',
  employmentType: 'employment_type',
  workRole: 'work_role',
  providerColor: 'provider_color',
  shiftStartTime: 'shift_start_time',
  shiftEndTime: 'shift_end_time',
  sameWorkingHours: 'same_working_hours',
  workStatus: 'work_status',
  workingSchedules: 'workingHours',
  operatoryRooms: 'operatory_rooms',
  isHidden: 'is_hidden',
  createdBy: 'created_by',
  updatedBy: 'updated_by',
  dateTimeCreatedAt: 'date_time_created_at',
  dateTimeUpdatedAt: 'date_time_updated_at',
}

/**
 * Maps partial ProviderData (e.g. only changed fields) to database shape.
 * Used for PUT update payloads that send only changed values.
 */
export const mapPartialProviderDataToDatabase = (
  partial: Partial<ProviderData>,
): Record<string, unknown> => {
  const result: Record<string, unknown> = {}
  for (const frontKey of Object.keys(partial) as (keyof ProviderData)[]) {
    if (frontKey === 'clinicIds') {
      const v = partial.clinicIds
      if (Array.isArray(v)) {
        result.clinic_ids = v
      }
      continue
    }
    if (frontKey === 'password' && typeof partial.password === 'string') {
      result.password = partial.password
      continue
    }
    const dbKey = PROVIDER_DATA_TO_DB_KEYS[frontKey]
    if (dbKey == null) continue
    const value = partial[frontKey]
    if (frontKey === 'workingSchedules' && Array.isArray(value)) {
      const providerId = partial.providerId
      result.workingHours = (value as WorkingSchedule[]).map((ws) => ({
        entity_type: WorkingHoursEntityType.PROVIDER,
        entity_id: providerId,
        day_of_week: ws.day_of_week,
        start_time: ws.start_time,
        end_time: ws.end_time,
      }))
    } else {
      result[dbKey as string] = value === '' ? null : value
    }
  }
  return result
}

export const mapProviderDataToDatabase = (
  providerData: ProviderData,
): Partial<Provider> & { clinic_ids?: string[] } => {
  const mappedData: Partial<Provider> & { clinic_ids?: string[] } = {
    provider_id: providerData.providerId,
    user_id: providerData.userId,
    clinic_id: providerData.clinicId,
    operatory_id: providerData.operatoryId,
    first_name: providerData.firstName,
    middle_name: providerData.middleName,
    last_name: providerData.lastName,
    username: providerData.username,
    gender: providerData.gender,
    date_of_birth: providerData.dateOfBirth,
    pronouns: providerData.pronouns,
    marital_status: providerData.maritalStatus,
    address: providerData.address,
    city: providerData.city,
    state: providerData.state,
    zip: providerData.zip,
    cellphone: providerData.mobile,
    email: providerData.email,
    home_number: providerData.phone,
    work_contact: providerData.work,
    fax_number: providerData.fax || null,
    state_license_number: providerData.stateLicenseNumber,
    state_where_licensed_issued: providerData.stateWhereLicensed,
    state_license_expiry: providerData.expiryDate,
    state_rx_id: providerData.stateRxId,
    dea_number: providerData.deaNumber,
    tax_id_type: providerData.taxIdType,
    tax_id_number: providerData.taxIdNumber,
    social_security_number: providerData.socialSecurityNumber,
    npi_type: providerData.npiType,
    npi_number: providerData.npiNumber,
    employment_type: providerData.employmentType,
    work_role: providerData.workRole,
    provider_color: providerData.providerColor,
    shift_start_time: providerData.shiftStartTime,
    shift_end_time: providerData.shiftEndTime,
    same_working_hours: providerData.sameWorkingHours,
    work_status: providerData.workStatus,
    workingHours: [
      ...(providerData.workingSchedules?.map((ws) => ({
        entity_type: WorkingHoursEntityType.PROVIDER,
        entity_id: providerData.providerId,
        day_of_week: ws.day_of_week,
        start_time: ws.start_time,
        end_time: ws.end_time,
      })) || []),
      ...(providerData.workOverrides?.map((o) => ({
        entity_type: WorkingHoursEntityType.PROVIDER,
        entity_id: providerData.providerId,
        day_of_week: o.date,
        start_time: o.start_time,
        end_time: o.end_time,
      })) || []),
    ],
    operatory_rooms: providerData.operatoryRooms,
    is_hidden: providerData.isHidden,
    created_by: providerData.createdBy,
    updated_by: providerData.updatedBy,
    date_time_created_at: providerData.dateTimeCreatedAt,
    date_time_updated_at: providerData.dateTimeUpdatedAt,
  }

  if (providerData.clinicIds != null && providerData.clinicIds.length > 0) {
    mappedData.clinic_ids = providerData.clinicIds
  }

  return mappedData
}

/**
 * Extracts personal information from full provider data
 */
export const extractPersonalInfo = (
  providerData: ProviderData,
): ProviderPersonalInfo => {
  return {
    firstName: providerData.firstName,
    lastName: providerData.lastName,
    middleName: providerData.middleName,
    username: providerData.username,
    gender: providerData.gender,
    dateOfBirth: providerData.dateOfBirth,
    pronouns: providerData.pronouns,
    maritalStatus: providerData.maritalStatus,
    address: providerData.address,
    city: providerData.city,
    state: providerData.state,
    zip: providerData.zip,
    mobile: providerData.mobile,
    email: providerData.email,
    phone: providerData.phone,
    work: providerData.work,
    fax: providerData.fax,
  }
}

/**
 * Extracts work information from full provider data
 */
export const extractWorkInfo = (
  providerData: ProviderData,
): ProviderWorkInfo => {
  return {
    stateLicenseNumber: providerData.stateLicenseNumber,
    stateWhereLicensed: providerData.stateWhereLicensed,
    expiryDate: providerData.expiryDate,
    stateRxId: providerData.stateRxId,
    deaNumber: providerData.deaNumber,
    taxIdType: providerData.taxIdType,
    taxIdNumber: providerData.taxIdNumber,
    socialSecurityNumber: providerData.socialSecurityNumber,
    npiType: providerData.npiType,
    npiNumber: providerData.npiNumber,
    employmentType: providerData.employmentType,
    providerColor: providerData.providerColor,
    sameWorkingHours: providerData.sameWorkingHours,
    workStatus: providerData.workStatus,
    workingSchedules: providerData.workingSchedules,
    shiftStartTime: providerData.shiftStartTime,
    shiftEndTime: providerData.shiftEndTime,
    operatoryId: providerData.operatoryId,
    workRole: providerData.workRole,
    clinicId: providerData.clinicId,
    workOverrides: providerData.workOverrides,
  }
}

/**
 * Extracts basic information for lists and cards
 */
export const extractBasicInfo = (
  providerData: ProviderData,
): ProviderBasicInfo => {
  return {
    providerId: providerData.providerId,
    firstName: providerData.firstName,
    lastName: providerData.lastName,
    middleName: providerData.middleName,
    username: providerData.username,
    email: providerData.email,
    mobile: providerData.mobile,
    providerColor: providerData.providerColor,
    isHidden: providerData.isHidden,
  }
}

/**
 * Creates a new provider data object with default values
 */
export const createEmptyProviderData = (): Partial<ProviderData> => {
  return {
    providerId: '',
    userId: '',
    clinicId: '',
    clinicIds: [],
    operatoryId: '',
    firstName: '',
    lastName: '',
    middleName: '',
    username: '',
    gender: '',
    dateOfBirth: '',
    pronouns: '',
    maritalStatus: '',
    address: '',
    city: '',
    state: '',
    zip: '',
    mobile: '',
    email: '',
    phone: '',
    work: '',
    fax: '',
    stateLicenseNumber: '',
    stateWhereLicensed: '',
    expiryDate: '',
    stateRxId: '',
    deaNumber: '',
    taxIdType: '',
    taxIdNumber: '',
    npiType: '',
    npiNumber: '',
    employmentType: '',
    workRole: '',
    providerColor: {
      header: '#D8352A',
      background: '#FFCBC8',
      text_color: '#470C08',
      border_color: 'var(--red-400)',
      label: 'Red',
    },

    sameWorkingHours: true,
    workStatus: WorkStatus.ACTIVE,
    workOverrides: [],
    operatoryRooms: '',
    isHidden: false,
  }
}
